import { useField, useForm } from "@formily/react";
import { Field } from "@formily/core";
// @ts-expect-error -- no type declarations available for react-signature-canvas
import SignatureCanvasImport from "react-signature-canvas";
import { useEffect, useRef } from "react";

// Handle CJS/ESM interop — in production builds, the default import may resolve
// to the module namespace object { default: Component } instead of the component itself.
const SignatureCanvas =
  "default" in SignatureCanvasImport
    ? (SignatureCanvasImport as Record<string, unknown>).default
    : SignatureCanvasImport;
import clsx from "clsx";
import { Button } from "./Button";
import { connect, mapProps } from "@formily/react";

interface RumbleSignatureProps {
  className?: string;
  minHeight?: number;
  readOnly?: boolean;
  filename?: string;
  value: string | null;
  onChange: (value: string | null) => void;
}

export const RumbleSignatureBase = ({
  className,
  minHeight = 220,
  readOnly = false,
  onChange,
  value,
}: RumbleSignatureProps) => {
  const form = useForm();
  const field = useField<Field>();
  const wrapRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/suspicious/noExplicitAny: react-signature-canvas has no type declarations
  const sigRef = useRef<any>(null);

  useEffect(() => {
    const sig = sigRef.current;
    if (!sig) return;

    if (!value) return;

    const img = new Image();
    img.src = value;
    img.onload = () => {
      sig.fromDataURL(value);
    };
    img.onerror = () => {
      onChange(null); // Clear signature on error
    };
  }, [form, field.props.name]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const sig = sigRef.current;
    if (!wrap || !sig) return;

    const resize = () => {
      const cssW = wrap.clientWidth;
      const cssH = Math.max(minHeight, Math.round(cssW / (16 / 10)));
      const dpr = window.devicePixelRatio || 1;

      const canvas = sig.getCanvas();
      const snapshot = !sig.isEmpty() ? canvas.toDataURL("image/png") : null;

      canvas.style.width = cssW + "px";
      canvas.style.height = cssH + "px";
      canvas.width = cssW * dpr;
      canvas.height = cssH * dpr;

      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (snapshot) sig.fromDataURL(snapshot);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [minHeight]);

  const handleChange = async () => {
    const canvas = sigRef.current?.getTrimmedCanvas();
    if (!canvas) return;

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b: Blob | null) => resolve(b), "image/png"),
    );

    if (blob) {
      const reader = new FileReader();
      const value = await new Promise<string | null>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => {
          console.error(
            "RumbleSignature.tsx: Failed to convert signature blob to data URL'",
          );
          reject(reader.error);
        };
        reader.readAsDataURL(blob);
      });

      onChange(value);
    } else {
      onChange(null);
    }
  };

  const clear = () => {
    sigRef.current?.clear();
    onChange(null);
  };

  return (
    <div className={clsx("mt-8", className)}>
      <div ref={wrapRef} className="w-full">
        <SignatureCanvas
          ref={sigRef}
          penColor="#111827"
          backgroundColor="#ffffff"
          onEnd={handleChange}
          clearOnResize={false}
          readOnly={readOnly}
          canvasProps={{
            className:
              "sigCanvas w-full rounded-lg bg-white border border-neutral-300 hover:border-neutral-400 focus:border-primary-500 active:border-primary-500 transition text-sm tracking-wide touch-none",
          }}
        />
      </div>
      {!readOnly && (
        <Button
          type="button"
          variant="greyLight"
          onClick={clear}
          className="mt-3 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
        >
          Handtekening wissen
        </Button>
      )}
    </div>
  );
};

export const RumbleSignature = connect(
  RumbleSignatureBase,
  mapProps((props) => ({
    ...props,
  })),
);

export default RumbleSignature;
